import { emptySplitApi } from "./emptyApi"

export type JobRunStatus =
  | "AWAITING_APPROVAL"
  | "SCHEDULED"
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "REJECTED"
  | "CANCELLED"

/** A job is still running while in one of these states. */
export const ACTIVE_JOB_STATUSES: JobRunStatus[] = [
  "AWAITING_APPROVAL",
  "SCHEDULED",
  "PENDING",
  "PROCESSING",
]

/** Rich detail captured for one created ad (the `result` of a per-ad-set blast). */
export interface JobAdSetResultDetail {
  /** The new Meta ad id created in this ad set. */
  newAdId?: string
  /** The creative id used / created for the new ad. */
  creativeId?: string
  newAdName?: string
  sourceAdName?: string
  strategy?: string
  status?: string
  /** When this individual ad was created (ISO). */
  createdAt?: string
  warnings?: string[]
  objectStoryId?: string
  sourceInstagramMediaId?: string
  [key: string]: unknown
}

/** One ad set's outcome inside an ad-blast job result. */
export interface JobAdSetResult {
  platform: string
  adSetId: string
  success: boolean
  /** Set on the creative-reblast path (newAdId lives in `result` for ad/post). */
  externalAdId?: string
  /** Owning account the blast recorded (`act_…` for Meta, advertiser id for TikTok) — drives the Ads Manager link. */
  adAccountId?: string
  /** Owning Meta Business id, when known. */
  businessId?: string
  error?: string
  requiredFields?: string[]
  result?: JobAdSetResultDetail
  /** Which source produced this row (present on batch blasts). */
  source?: string
  kind?: "ad" | "post" | "creative" | "adCreative"
  /** Name of the ad set (set on 1:1 blasts; `adSetId` is "" when creating it failed). */
  adSetName?: string
  /** True when the blast created this ad set itself (1:1 ad sets, MD-5426). */
  createdAdSet?: boolean
  /** Non-fatal notes from ad-set creation (e.g. an audience that couldn't be resolved). */
  warnings?: string[]
}

/**
 * Meta ad-level creative test riding on an ad-blast batch (MD-5569): after
 * every unit lands in the ONE target ad set, the job publishes a
 * SPLIT_TEST_V2 ad study whose cells are the new ads. Budget is in the
 * account's minor unit (cents); exactly one of the two budget fields is set.
 */
export interface CreativeTestInput {
  name: string
  description?: string
  dailyBudgetCents?: number
  lifetimeBudgetPercentage?: number
  /** Unix seconds, in the future. */
  startTime: number
  /** Unix seconds, after startTime. */
  endTime: number
}

/** Outcome of the creative-test phase on a completed ad-blast job. */
export interface AdBlastCreativeTestResult {
  spec: CreativeTestInput
  adSetId: string
  /** `skipped` = a variant failed so the study was never published; `failed` = Meta refused it. */
  status: "pending" | "created" | "skipped" | "failed"
  studyId?: string
  cells?: { cellId: string; adId: string; treatmentPercentage: number }[]
  error?: string
}

export interface AdBlastJobResult {
  /** Every source blasted (1 for a single blast, N for a batch). */
  sources?: {
    kind: "ad" | "post" | "creative" | "adCreative"
    source: string
  }[]
  /** Convenience for the single-source case (undefined for a batch). */
  kind?: "ad" | "post" | "creative" | "adCreative"
  source?: string
  results: JobAdSetResult[]
  summary: { total: number; successful: number; failed: number }
  /** Present only on creative-test batches. */
  creativeTest?: AdBlastCreativeTestResult
}

/** Standardized audience shape of a 1:1 ad set (MD-5426). */
export type OneToOneAdSetType =
  | "LUKEWARM_PROSPECTING"
  | "COLD_PROSPECTING"
  | "WARM_PROSPECTING"
  | "RETENTION"

/**
 * "Launch each ad into its own 1:1 ad set" (MD-5426): the backend creates
 * one new PAUSED ad set per source × campaign (targeting / placements / pixel
 * copied from the campaign's newest ad set, audiences by `adSetType`,
 * incremental attribution) and blasts the ad into it. Mutually exclusive with
 * `target_adsets`.
 */
export interface OneToOneAdSetsInput {
  /** Meta campaign ids to create the ad sets under (at least one). */
  campaignIds: string[]
  adSetType: OneToOneAdSetType
  /** Meta optimization goal wire value (e.g. "OFFSITE_CONVERSIONS"). */
  optimizationGoal: string
  /** Daily minimum budget in dollars (> 0). */
  dailyBudget: number
}

/** Payload for triggering an ad-blast job (single or bulk). */
interface BlastDtoLike {
  /** Existing ad sets to blast into, keyed by platform. Omit when `one_to_one_adsets` is set. */
  target_adsets?: Record<string, string[]>
  /** Create one new ad set per campaign instead of targeting existing ones. */
  one_to_one_adsets?: OneToOneAdSetsInput
  overrides?: Record<string, unknown>
  adName?: string
  /** Post blasts: which network the post lives on (lets the API resolve bare Facebook post ids). */
  channel?: "facebook" | "instagram"
  /** Post blasts: tag selection (category slug → option ids) applied to every ad created. */
  tags?: Record<string, string[]>
  /**
   * Post blasts: cached media from our own data, used by the rebuild path
   * when Meta won't let the token read the post (or the post can't be
   * boosted as-is). Mirrors BlastPostDto.
   */
  imageUrl?: string
  caption?: string
  videoUrl?: string
  thumbnailUrl?: string
  mediaType?: "image" | "video" | "carousel"
}
export type AdBlastUnitInput =
  | { kind: "ad"; platform: string; adId: string; dto: BlastDtoLike }
  | { kind: "post"; platform: string; postId: string; dto: BlastDtoLike }
  | { kind: "creative"; creativeId: string; dto: BlastDtoLike }
  | { kind: "adCreative"; adCreativeId: string; dto: BlastDtoLike }
export type AdBlastTriggerInput =
  | AdBlastUnitInput
  | {
      kind: "batch"
      units: AdBlastUnitInput[]
      /** Run the batch as a Meta creative test — every unit must target the same single Meta ad set. */
      creativeTest?: CreativeTestInput
    }

/**
 * Args for the ad-blast trigger. `at` schedules a one-shot future run — the
 * backend accepts it as a `?at=<ISO>` query param and lands the job in
 * SCHEDULED status until due. Omit `at` to blast immediately.
 */
export interface AdBlastTriggerArgs {
  input: AdBlastTriggerInput
  /** ISO-8601 UTC instant to run at; omit to run now. */
  at?: string
}

/** Payload for triggering the AdForge image-gen job (enhance + extend). */
export interface ImageGenCardInput {
  parentAssetId: string
  enhanceMain?: { prompt: string; aspectRatio: string }
  placements?: Array<{ slot: string; aspectRatio: string; prompt: string }>
}
export interface ImageGenTriggerInput {
  cards: ImageGenCardInput[]
}

/** Page kinds the holoscanner crawler accepts (mirrors backend HoloscannerPageType). */
export type HoloscannerPageType = "SHOPIFY_PRODUCT_PAGE" | "CONTENT_PAGE"

/** One page to crawl on demand. `productId` is only meaningful for product pages. */
export interface HoloscannerCrawlRequest {
  url: string
  pageType: HoloscannerPageType
  productId?: string
}

/** Payload for triggering the holoscanner-crawl job (a batch of pages). */
export interface HoloscannerCrawlInput {
  requests: HoloscannerCrawlRequest[]
}

/** Payload for the holoscanner-crawl-check poller (waits for one page to land). */
export interface HoloscannerCrawlCheckInput {
  url: string
}

/** Mirrors the backend JobRunStatusView (GET /jobs/:id). */
/** Counts every job result carries (the part that's common across job types). */
export interface JobSummaryCounts {
  total: number
  successful: number
  failed: number
}

/**
 * Generic job result. `summary` is common to every job type; the rest of the
 * shape is type-specific and is narrowed by the per-type view renderers
 * (see pages/Jobs/jobViews). Cast to `AdBlastJobResult` / `ImageGenJobResult`
 * etc. inside those renderers.
 */
export interface JobResult {
  summary?: JobSummaryCounts
  [key: string]: unknown
}

/** ad-forge-image-gen result row (one generated image). */
export interface ImageGenResultRow {
  parentAssetId: string
  kind: "main" | "placement"
  slot?: string
  aspectRatio: string
  success: boolean
  newAssetId?: string
  error?: string
}
export interface ImageGenJobResult {
  results: ImageGenResultRow[]
  summary: JobSummaryCounts
}

export interface JobRunView {
  id: string
  type: string
  status: JobRunStatus
  step: string | null
  result: JobResult | null
  error: string | null
  /** When a SCHEDULED job is due to run (ISO); null for non-scheduled jobs. */
  scheduledFor: string | null
  enqueuedAt: string
  processingStartedAt: string | null
  completedAt: string | null
}

export interface ListJobsArgs {
  type?: string
  status?: JobRunStatus
  /** Only jobs created at/after this ISO datetime. */
  from?: string
  /** Only jobs created at/before this ISO datetime. */
  to?: string
  take?: number
  skip?: number
}

/** One QA finding from a meta-ads-qa job result. */
export interface MetaAdsQaFinding {
  adId: string
  adName: string
  code: string
  severity: "error" | "warning" | "info"
  message: string
  snippet?: string
  suggestion?: string
  source: "rule" | "ai"
  agent?: string
  /**
   * The flagged attribute comes from the ad's existing post and can't be
   * edited on the ad (Meta locks post content while ads use it) — capped at
   * warning; the fix is editing or replacing the post.
   */
  postLocked?: boolean
}

/**
 * The meta-ads-qa JobRun result (subset the Jobs page renders). `ignored`
 * carries the ads marked irrelevant — their findings are excluded from the
 * counts/findings here but recorded so hidden work stays visible.
 */
export interface MetaAdsQaResult {
  adsChecked: number
  adsWithFindings: number
  totalFindings: number
  bySeverity: { error: number; warning: number; info: number }
  findings: MetaAdsQaFinding[]
  spendByAdId?: Record<string, number>
  statusByAdId?: Record<string, string>
  adAccountId?: string
  /** Findings on post-locked attributes (not fixable on the ad). Absent on old results. */
  postLockedFindings?: number
  /** Absent on results from before the ignore feature shipped. */
  ignored?: {
    adCount: number
    findingCount: number
    ads: Array<{ adId: string; adName: string; hiddenFindings: number }>
    totalForCompany: number
  }
  /**
   * Ads marked done (reviewed and closed out) — skipped before review, so
   * not in adsChecked. Absent on results from before the feature shipped.
   */
  done?: {
    skippedCount: number
    hiddenFindings: number
    ads: Array<{ adId: string; adName: string; markedAt: string }>
    totalForCompany: number
  }
  coverage?: {
    /**
     * UTM check against the company pattern (Settings → Company → UTM
     * parameters) with the auto-fix counters. Absent on results from before
     * the check shipped.
     */
    utms?: {
      mode: "pattern" | "none"
      pattern: string
      autoFix: boolean
      checked: number
      compliant: number
      notApplicable: number
      fixed: number
      fixFailed: number
      unfixable: number
      notFixed: number
      error?: string
    }
  }
  /** url_tags rewrites the run attempted — the only change the QA makes on Meta. Absent on old results. */
  utmFixes?: Array<{
    adId: string
    adName: string
    before: string
    after: string
    ok: boolean
    error?: string
  }>
}

/** An ad marked irrelevant for the Meta Ads QA report. */
export interface AdsQaIgnoredAd {
  adId: string
  adName?: string
  markedAt: string
}

/** Input for the analyze-creative job (POST /jobs/analyze-creative). */
export interface AnalyzeCreativeTriggerInput {
  /** Platform ad ids, max 100 per run. */
  adIds: string[]
  /** One platform per run (default meta). */
  platform?: "meta" | "tiktok" | "youtube"
  prompt?: string
  /** Persist each report onto the ad's Asset-library row. */
  save?: boolean
  /** Write the tag suggestions onto each ad as tag assignments (source ai_suggested). */
  autoApplyTags?: boolean
  /**
   * Reuse an ad's saved analysis instead of sending it through Gemini again;
   * its stored suggestions still feed autoApplyTags. Off = re-analyze everything.
   */
  skipAnalyzed?: boolean
}

/** One ad's row in an analyze-creative job result. */
export interface AnalyzeCreativeResultRow {
  adId: string
  adName?: string
  /** `text` = the creative could not be read; the report rests on the ad's metadata. */
  mediaType?: "video" | "image" | "text"
  saved: boolean
  saveError?: string
  /** No Gemini call: the stored report (from `analyzedAt`) was reused. */
  reusedSavedAnalysis?: boolean
  analyzedAt?: string | null
  /** The full Gemini report (markdown), with the server-rendered Suggested Tags section. */
  analysis?: string
  suggestedTags?: {
    category: string
    options: string[]
    reason?: string
    /** creative, or the ad-metadata field that decided the pick (ad_name, …). */
    source?: string
  }[]
  suggestedMediaCategory?: string
  assetId?: string
  tagsApplied?: number
  /** The picks written, as `Category: Option`. */
  tagsWritten?: string[]
  /** Categories left alone because a human had already tagged them. */
  tagsKept?: string[]
  tagApplyError?: string
  tagApplySkippedReason?: string
  error?: string
}

export interface AnalyzeCreativeJobResult {
  platform: string
  results: AnalyzeCreativeResultRow[]
  summary: JobSummaryCounts & {
    analyzed: number
    reused: number
    saved: number
    tagsApplied?: number
    tagged?: number
    untagged?: number
  }
}

/** Input for the meta-ads-qa job. All optional — server defaults apply. */
export interface MetaAdsQaTriggerInput {
  /** created/updated window in hours (server default 168; the server never goes below 7 days). */
  lookbackHours?: number
  maxAds?: number
  /** Skip the Gemini copy review; rule checks only. */
  skipAi?: boolean
  /** Custom Slack channel for the findings digest (unset -> jobs channel). */
  slackChannelId?: string
  /**
   * Rewrite url_tags on ads whose UTMs don't match the company pattern.
   * Server default false: the run only suggests the expected url_tags
   * (applied on click from the job run).
   */
  autoFixUtms?: boolean
}

/**
 * Recurring schedule row (GET /jobs/schedules). The LIST endpoint strips
 * `input`; fetch a single schedule (getJobSchedule) to read it back.
 */
export interface JobScheduleView {
  id: string
  type: string
  cron: string
  timezone: string | null
  enabled: boolean
  nextRunAt: string
  lastRunAt: string | null
  createdAt: string
  /** Present only on the single-schedule endpoint. */
  input?: unknown
}

/** One registered job type (GET /jobs/types — super-admin discovery). */
export interface JobTypeView {
  type: string
  scopes: string[]
  description?: string
  requiresApproval: boolean
}

/**
 * Admin schedule row (GET /jobs/admin/schedules) — cross-company, always
 * includes the `input` payload plus the owning company and creator.
 */
export interface AdminJobScheduleView {
  id: string
  companyId: string
  companyName: string
  type: string
  cron: string
  timezone: string | null
  enabled: boolean
  nextRunAt: string
  lastRunAt: string | null
  createdAt: string
  createdBy: string | null
  input: unknown
}

export const jobsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getJob: builder.query<JobRunView, string>({
      query: id => `jobs/${id}`,
    }),
    /**
     * Cancel an active job (awaiting/scheduled/queued/running). Terminal jobs
     * return 409. Returns the updated job view.
     */
    cancelJob: builder.mutation<{ id: string; status: JobRunStatus }, string>({
      query: id => ({ url: `jobs/${id}/cancel`, method: "POST" }),
    }),
    /**
     * Trigger an ad-blast job (single source, or a `batch` of sources so
     * multiple selected creatives/ads/posts run as ONE job). Hits the generic
     * job-trigger endpoint; returns the job id to poll / deep-link to. Pass
     * `at` to schedule a one-shot future run (job lands in SCHEDULED status).
     */
    triggerAdBlast: builder.mutation<
      { jobRunId: string; status: string },
      AdBlastTriggerArgs
    >({
      query: ({ input, at }) => ({
        url: at
          ? `jobs/ad-blast?at=${encodeURIComponent(at)}`
          : "jobs/ad-blast",
        method: "POST",
        body: input,
      }),
    }),
    /**
     * Trigger the AdForge image-gen job — enhance + extend a batch of cards in
     * the background. Returns the job id to poll; results persist as GEN_AI
     * variants so the editor can leave and pick them up on return.
     */
    triggerImageGen: builder.mutation<
      { jobRunId: string; status: string },
      ImageGenTriggerInput
    >({
      query: body => ({ url: "jobs/ad-forge-image-gen", method: "POST", body }),
    }),
    /**
     * Trigger an on-demand holoscanner crawl for one or more pages. The job
     * only ENQUEUES the requests; the crawl runs out-of-band (an Airflow sensor
     * picks them up ~every 2 min), so freshly crawled assets are not available
     * immediately. AdForge uses this when "Add assets from URL" finds no
     * existing page snapshot. Returns the job id to poll at GET /jobs/:id.
     */
    triggerHoloscannerCrawl: builder.mutation<
      { jobRunId: string; status: string },
      HoloscannerCrawlInput
    >({
      query: body => ({ url: "jobs/holoscanner-crawl", method: "POST", body }),
    }),
    /**
     * Trigger the crawl-check poller for one page. After a crawl is enqueued,
     * this job polls (every 30s, server-side) until the page snapshot + assets
     * land, then notifies the user via the Jobs inbox. Returns the job id.
     */
    triggerHoloscannerCrawlCheck: builder.mutation<
      { jobRunId: string; status: string },
      HoloscannerCrawlCheckInput
    >({
      query: body => ({
        url: "jobs/holoscanner-crawl-check",
        method: "POST",
        body,
      }),
    }),
    /**
     * Trigger the meta-ads-qa job — QA the company's recently-created Meta
     * ads (CTA, copy, destination URL, char limits, Meta issues, AI typo
     * review). One run per company at a time (server-side dedupe returns the
     * existing job). Findings land in the job result (Jobs page) and Slack.
     */
    /**
     * Gemini creative analysis for a list of ads as ONE durable job (one ad
     * per step, ~30-90s per video). Results land in the job result (Jobs
     * page) and, with `save`, on each ad's asset row.
     */
    triggerAnalyzeCreative: builder.mutation<
      { jobRunId: string; status: string },
      AnalyzeCreativeTriggerInput
    >({
      query: body => ({
        url: "jobs/analyze-creative",
        method: "POST",
        body,
      }),
    }),
    triggerMetaAdsQa: builder.mutation<
      { jobRunId: string; status: string },
      MetaAdsQaTriggerInput | void
    >({
      query: body => ({
        url: "jobs/meta-ads-qa",
        method: "POST",
        body: body ?? {},
      }),
    }),
    /** List this company's recurring schedules, optionally by job type. */
    listJobSchedules: builder.query<
      JobScheduleView[],
      { type?: string } | void
    >({
      query: arg => ({
        url: "jobs/schedules",
        params: arg?.type ? { type: arg.type } : undefined,
      }),
      providesTags: ["JobSchedules"],
    }),
    /** One schedule INCLUDING its input payload (the list strips it). */
    getJobSchedule: builder.query<JobScheduleView, string>({
      query: id => `jobs/schedules/${id}`,
      providesTags: ["JobSchedules"],
    }),
    /**
     * Every schedule of a type WITH its input payload. The list endpoint strips
     * `input`, so this fans out to the single-schedule endpoint per row — the
     * only way to tell apart schedules that differ solely by input (e.g. WHICH
     * custom report a Slack digest belongs to). Row counts stay small: schedules
     * are capped at 100 per company and this filters by type first.
     */
    listJobSchedulesWithInput: builder.query<
      JobScheduleView[],
      { type: string }
    >({
      queryFn: async ({ type }, _api, _extraOptions, baseQuery) => {
        const list = await baseQuery({
          url: "jobs/schedules",
          params: { type },
        })
        if (list.error) return { error: list.error }
        const rows = (list.data as JobScheduleView[]) ?? []
        const hydrated = await Promise.all(
          rows.map(row => baseQuery(`jobs/schedules/${row.id}`)),
        )
        // Fail the whole query on any row error rather than silently returning a
        // short list — a missing row would read as "notifications are off".
        const failed = hydrated.find(result => result.error)
        if (failed?.error) return { error: failed.error }
        return { data: hydrated.map(result => result.data as JobScheduleView) }
      },
      providesTags: ["JobSchedules"],
    }),
    createJobSchedule: builder.mutation<
      JobScheduleView,
      { type: string; cron: string; input?: unknown; timezone?: string }
    >({
      query: ({ type, ...body }) => ({
        url: `jobs/${type}/schedules`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["JobSchedules"],
    }),
    /**
     * Patch a schedule in place. `input` is patchable too, so re-pointing a
     * schedule takes ONE call — deleting and re-creating it loses the schedule
     * whenever the create is then rejected.
     */
    updateJobSchedule: builder.mutation<
      JobScheduleView,
      {
        id: string
        enabled?: boolean
        cron?: string
        timezone?: string
        input?: unknown
      }
    >({
      query: ({ id, ...body }) => ({
        url: `jobs/schedules/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["JobSchedules"],
    }),
    deleteJobSchedule: builder.mutation<{ deleted: boolean }, string>({
      query: id => ({ url: `jobs/schedules/${id}`, method: "DELETE" }),
      invalidatesTags: ["JobSchedules"],
    }),
    /**
     * Fire a schedule immediately — same input and enqueue path as its cron
     * tick (nextRunAt untouched). For testing scheduled jobs on demand.
     */
    runJobScheduleNow: builder.mutation<
      { jobRunId: string; status: string },
      string
    >({
      query: id => ({ url: `jobs/schedules/${id}/run-now`, method: "POST" }),
    }),
    // --- Admin (super-admin only) schedule management — cross-company. ---
    /** Registered job types (for the admin create-schedule type picker). */
    listJobTypes: builder.query<JobTypeView[], void>({
      query: () => "jobs/types",
    }),
    listAdminJobSchedules: builder.query<
      AdminJobScheduleView[],
      { type?: string } | void
    >({
      query: arg => ({
        url: "jobs/admin/schedules",
        params: arg?.type ? { type: arg.type } : undefined,
      }),
      providesTags: ["JobSchedules"],
    }),
    createAdminJobSchedule: builder.mutation<
      AdminJobScheduleView,
      {
        type: string
        companyId: string
        cron: string
        input?: unknown
        timezone?: string
      }
    >({
      query: body => ({ url: "jobs/admin/schedules", method: "POST", body }),
      invalidatesTags: ["JobSchedules"],
    }),
    /** Unlike the company-scoped PATCH, this can also replace `input`. */
    updateAdminJobSchedule: builder.mutation<
      AdminJobScheduleView,
      {
        id: string
        enabled?: boolean
        cron?: string
        timezone?: string
        input?: unknown
      }
    >({
      query: ({ id, ...body }) => ({
        url: `jobs/admin/schedules/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["JobSchedules"],
    }),
    deleteAdminJobSchedule: builder.mutation<{ deleted: boolean }, string>({
      query: id => ({ url: `jobs/admin/schedules/${id}`, method: "DELETE" }),
      invalidatesTags: ["JobSchedules"],
    }),
    /** Fires under the schedule's own company (not the admin's session company). */
    runAdminJobScheduleNow: builder.mutation<
      { jobRunId: string; status: string },
      string
    >({
      query: id => ({
        url: `jobs/admin/schedules/${id}/run-now`,
        method: "POST",
      }),
    }),
    /** Ads marked irrelevant for the Meta Ads QA report (company-scoped). */
    listAdsQaIgnoredAds: builder.query<AdsQaIgnoredAd[], void>({
      query: () => "ads-qa/ignored-ads",
      providesTags: ["AdsQaIgnoredAds"],
    }),
    /** Mark an ad irrelevant — future QA reports hide its findings. Returns the updated list. */
    markAdsQaIgnoredAd: builder.mutation<
      AdsQaIgnoredAd[],
      { adId: string; adName?: string }
    >({
      query: body => ({ url: "ads-qa/ignored-ads", method: "POST", body }),
      invalidatesTags: ["AdsQaIgnoredAds"],
    }),
    /** Re-include an ad — future QA reports show its findings again. */
    unmarkAdsQaIgnoredAd: builder.mutation<AdsQaIgnoredAd[], string>({
      query: adId => ({ url: `ads-qa/ignored-ads/${adId}`, method: "DELETE" }),
      invalidatesTags: ["AdsQaIgnoredAds"],
    }),
    /** Ads marked done for the Meta Ads QA report — future QA runs skip them. */
    listAdsQaDoneAds: builder.query<AdsQaIgnoredAd[], void>({
      query: () => "ads-qa/done-ads",
      providesTags: ["AdsQaDoneAds"],
    }),
    /** Mark an ad done — closed out; future QA runs skip it. Returns the updated list. */
    markAdsQaDoneAd: builder.mutation<
      AdsQaIgnoredAd[],
      { adId: string; adName?: string }
    >({
      query: body => ({ url: "ads-qa/done-ads", method: "POST", body }),
      invalidatesTags: ["AdsQaDoneAds"],
    }),
    /** Re-open a done ad — future QA runs review it again. */
    unmarkAdsQaDoneAd: builder.mutation<AdsQaIgnoredAd[], string>({
      query: adId => ({ url: `ads-qa/done-ads/${adId}`, method: "DELETE" }),
      invalidatesTags: ["AdsQaDoneAds"],
    }),
    listJobs: builder.query<JobRunView[], ListJobsArgs | void>({
      query: arg => {
        const args: ListJobsArgs = arg || {}
        const params: Record<string, string> = {}
        if (args.type) params.type = args.type
        if (args.status) params.status = args.status
        if (args.from) params.from = args.from
        if (args.to) params.to = args.to
        if (args.take !== undefined) params.take = String(args.take)
        if (args.skip !== undefined) params.skip = String(args.skip)
        return {
          url: "jobs",
          params: Object.keys(params).length ? params : undefined,
        }
      },
    }),
  }),
})

export const {
  useGetJobQuery,
  useLazyGetJobQuery,
  useCancelJobMutation,
  useListJobsQuery,
  useTriggerAdBlastMutation,
  useTriggerImageGenMutation,
  useTriggerHoloscannerCrawlMutation,
  useTriggerHoloscannerCrawlCheckMutation,
  useTriggerMetaAdsQaMutation,
  useTriggerAnalyzeCreativeMutation,
  useListJobSchedulesQuery,
  useGetJobScheduleQuery,
  useListJobSchedulesWithInputQuery,
  useCreateJobScheduleMutation,
  useUpdateJobScheduleMutation,
  useDeleteJobScheduleMutation,
  useRunJobScheduleNowMutation,
  useListJobTypesQuery,
  useListAdminJobSchedulesQuery,
  useCreateAdminJobScheduleMutation,
  useUpdateAdminJobScheduleMutation,
  useDeleteAdminJobScheduleMutation,
  useRunAdminJobScheduleNowMutation,
  useListAdsQaIgnoredAdsQuery,
  useMarkAdsQaIgnoredAdMutation,
  useUnmarkAdsQaIgnoredAdMutation,
  useListAdsQaDoneAdsQuery,
  useMarkAdsQaDoneAdMutation,
  useUnmarkAdsQaDoneAdMutation,
} = jobsApi
